import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, FileSignature, Route as RouteIcon, MessageCircle, CalendarClock } from "lucide-react";
import { api } from "../lib/api";
import { usePersonalization } from "../lib/personalization";
import { useContent } from "../lib/content";
import { useBranding } from "../components/Layout";

const UC_FIELDS = [
  { key: "inspection", label: "Inspection", hasTime: true },
  { key: "appraisal", label: "Appraisal", hasTime: true },
  { key: "due_diligence_deadline", label: "Due Diligence Deadline", hasTime: false },
  { key: "finance_appraisal_deadline", label: "Finance & Appraisal Deadline", hasTime: false },
  { key: "settlement_deadline", label: "Settlement Deadline", hasTime: false },
];

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" });
};
const formatTime = (t) => {
  if (!t) return "";
  const s = String(t).trim();
  // Free-text passthrough: if it's not a strict HH:MM, render exactly what the agent typed
  // (so "11am – 1pm", "11-1", "between 5 and 6 PM", etc. all just work).
  if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s;
  const [hh, mm] = s.split(":");
  const hr = parseInt(hh, 10);
  const ampm = hr >= 12 ? "PM" : "AM";
  const h12 = ((hr + 11) % 12) + 1;
  return `${h12}:${mm} ${ampm}`;
};
const countdownStr = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const diff = Math.round((d - now) / 86400000);
  if (diff > 0) return `in ${diff} day${diff === 1 ? "" : "s"}`;
  if (diff === 0) return "today";
  return `${-diff} day${diff === -1 ? "" : "s"} ago`;
};

export default function ImportantDates() {
  const { p } = usePersonalization();
  const { content } = useContent();
  const branding = useBranding();
  const [showings, setShowings] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/me/showings").then(({ data }) => { if (alive) setShowings(data.showings || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const upcomingShowings = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return showings.filter((s) => s.status !== "cancelled" && s.date >= today);
  }, [showings]);

  const keyDates = useMemo(() => {
    const dateFields = [
      ...(content.date_fields || []).filter((f) => !(p.hidden_date_fields || []).includes(f.key)),
      ...(p.custom_date_fields || []),
    ];
    return dateFields
      .map((f) => ({ ...f, value: p.key_dates?.[f.key], time: p.key_dates_times?.[f.key] }))
      .filter((f) => f.value)
      // Chronological — earliest milestone first, regardless of CMS
      // config order. Tie-break by time-of-day so two events on the same
      // day sit in the order they'll actually happen.
      .sort((a, b) => (a.value !== b.value ? (a.value < b.value ? -1 : 1)
                                            : (a.time || "").localeCompare(b.time || "")));
  }, [content.date_fields, p.hidden_date_fields, p.custom_date_fields, p.key_dates, p.key_dates_times]);

  const stageDates = useMemo(() => {
    const stages = content.timeline || [];
    return stages
      .map((s) => ({ id: s.id, label: s.title, value: p.key_dates?.[`stage_${s.id}`], time: p.key_dates_times?.[`stage_${s.id}`] }))
      .filter((s) => s.value)
      .sort((a, b) => (a.value !== b.value ? (a.value < b.value ? -1 : 1)
                                            : (a.time || "").localeCompare(b.time || "")));
  }, [content.timeline, p.key_dates, p.key_dates_times]);

  const underContract = useMemo(() => {
    const uc = p.under_contract || {};
    if (!uc.active) return [];
    return UC_FIELDS
      .map((f) => ({ ...f, ...(uc[f.key] || {}) }))
      .filter((it) => it.date);
  }, [p.under_contract]);

  // Sort the combined "all upcoming" list chronologically for the hero countdown.
  const allSorted = useMemo(() => {
    const flat = [
      ...keyDates.map((k) => ({ kind: "key", label: k.label, date: k.value, time: k.time })),
      ...stageDates.map((s) => ({ kind: "stage", label: s.label, date: s.value, time: s.time })),
      ...underContract.map((u) => ({ kind: "uc", label: u.label, date: u.date, time: u.time })),
      ...upcomingShowings.map((s) => ({ kind: "showing", label: `Showing${s.buyer_agent ? ` · ${s.buyer_agent}` : ""}`, date: s.date, time: s.start_time })),
    ];
    return flat.filter((x) => x.date).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [keyDates, stageDates, underContract, upcomingShowings]);
  const nextUp = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return allSorted.find((x) => x.date >= today);
  }, [allSorted]);

  const isEmpty = keyDates.length === 0 && stageDates.length === 0 && underContract.length === 0 && upcomingShowings.length === 0;

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <span className="overline">Important dates</span>
      <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        Everything on your calendar.
      </h1>
      <p style={{ color: "var(--text-muted)", maxWidth: 720, fontSize: "1.05rem" }}>
        All the dates {branding.agent_first_name} has set for your sale, in one place. This page updates automatically whenever they add or change a date.
      </p>

      {nextUp && (
        <div className="card" style={{ padding: 24, marginTop: 28, background: "var(--surface-alt)", boxShadow: "none" }} data-testid="dates-next-up">
          <div className="overline" style={{ color: "var(--primary)" }}>Next up</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
            <span className="font-serif" style={{ fontSize: 24 }}>{nextUp.label}</span>
            <span style={{ color: "var(--primary)", fontWeight: 600 }}>{countdownStr(nextUp.date)}</span>
          </div>
          <div style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>
            {formatDate(nextUp.date)}{nextUp.time ? ` · ${formatTime(nextUp.time)}` : ""}
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="card" style={{ padding: 32, marginTop: 28, color: "var(--text-muted)" }} data-testid="dates-empty">
          <p style={{ margin: 0 }}>
            No dates yet. {branding.agent_first_name} will add your strategy meeting, photo day, listing launch, showings, and key milestones here as they're scheduled.
          </p>
          <Link to="/ask" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--primary)", marginTop: 14, fontSize: 14 }}>
            <MessageCircle size={14} /> Ask {branding.agent_first_name} about your timeline
          </Link>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 28 }}>
          {upcomingShowings.length > 0 && (
            <Section
              title="Showings"
              icon={<CalendarClock size={18} />}
              testid="dates-section-showings"
              hint="Buyers coming through. You'll get an email reminder the night before and 2 hours ahead."
            >
              {upcomingShowings.map((s) => {
                const buyerLine = [s.buyer_agent, s.buyer_brokerage].filter(Boolean).join(" · ");
                const desc = [buyerLine, s.note].filter(Boolean).join(" — ");
                return (
                  <DateRow
                    key={s.id}
                    label={s.buyer_agent ? `Showing · ${s.buyer_agent}` : "Showing"}
                    date={s.date}
                    time={s.start_time}
                    description={desc || undefined}
                    testid={`date-row-showing-${s.id}`}
                  />
                );
              })}
            </Section>
          )}

          {underContract.length > 0 && (
            <Section
              title="Contract milestones"
              icon={<FileSignature size={18} />}
              accent="var(--secondary)"
              testid="dates-section-contract"
              hint="Dates and deadlines for the active contract."
            >
              {underContract.map((it) => (
                <DateRow
                  key={it.key}
                  label={it.label}
                  date={it.date}
                  time={it.time}
                  description={it.description}
                  testid={`date-row-uc-${it.key}`}
                  accent="var(--secondary)"
                />
              ))}
            </Section>
          )}

          {keyDates.length > 0 && (
            <Section
              title="Key dates"
              icon={<Calendar size={18} />}
              testid="dates-section-key"
              hint="Strategy meeting, staging, photo day, and other big moments."
            >
              {keyDates.map((it) => (
                <DateRow
                  key={it.key}
                  label={it.label}
                  date={it.value}
                  time={it.time}
                  testid={`date-row-key-${it.key}`}
                />
              ))}
            </Section>
          )}

          {stageDates.length > 0 && (
            <Section
              title="Timeline stages"
              icon={<RouteIcon size={18} />}
              testid="dates-section-stages"
              hint="When each stage of your selling process is happening."
            >
              {stageDates.map((it, i) => (
                <DateRow
                  key={it.id}
                  label={`0${i + 1} · ${it.label}`}
                  date={it.value}
                  time={it.time}
                  testid={`date-row-stage-${it.id}`}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, hint, accent, testid, children }) {
  return (
    <div className="card" style={{ overflow: "hidden" }} data-testid={testid}>
      <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: accent || "var(--primary)" }}>{icon}</span>
          <span className="font-serif" style={{ fontSize: 22, fontWeight: 500 }}>{title}</span>
        </div>
        {hint && <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function DateRow({ label, date, time, description, testid, accent }) {
  return (
    <div
      style={{
        padding: "16px 24px",
        borderTop: "1px solid var(--border)",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 12,
        alignItems: "center",
        borderLeft: accent ? `3px solid ${accent}` : undefined,
      }}
      data-testid={testid}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{label}</div>
        {description && <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>{description}</div>}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 500 }}>{formatDate(date)}{time ? ` · ${formatTime(time)}` : ""}</div>
        <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{countdownStr(date)}</div>
      </div>
    </div>
  );
}
